import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { faceScan } from "careplix-web-algo";
import { ReactComponent as CarePlix } from "../assets/icons/PoweredByCarePlix.svg";
import Loader from "../assets/images/loader.gif";
import Analyzing from "../assets/images/analyzing.webp";
import Failure from "../assets/images/failure.webp";
import { postMessage } from "../utils/helper";

const FaceScan = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const moduleState = state?.moduleState || {};

  const canvasRef = useRef(null);
  const previewVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const faceMatchDoneRef = useRef(false);
  const recordingStartedRef = useRef(false);
  const consentScrollRef = useRef(null);
  const videoOnlyStreamRef = useRef(null);
  const isUnmountedRef = useRef(false);

  const [consentText, setConsentText] = useState("");
  const [isConsentGiven, setIsConsentGiven] = useState(false);
  const [readyToProcess, setReadyToProcess] = useState(false);
  const [scanData, setScanData] = useState(null);
  const [isFaceMatched, setIsFaceMatched] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [autoSubmitCountdown, setAutoSubmitCountdown] = useState(null);
  const [isVideoOnly, setIsVideoOnly] = useState(false);

  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [scanCompleted, setScanCompleted] = useState(false);
  const [count, setCount] = useState(3);
  const [phase, setPhase] = useState("instruction");

  const [scanFrameData, setScanFrameData] = useState({
    type: "",
    message: "",
    percentage: 0,
    timeElapsed: 0,
    isLightMode: false,
    fps: 0,
  });

  const delay = (ms) => new Promise((r) => setTimeout(r, ms));

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
    });

  const discardRecording = () => {
    recordingStartedRef.current = false;

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.stop();
    }
    mediaRecorderRef.current = null;
    mediaChunksRef.current = [];

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }

    if (videoOnlyStreamRef.current) {
      videoOnlyStreamRef.current.getTracks().forEach((t) => t.stop());
      videoOnlyStreamRef.current = null;
    }

    if (previewVideoRef.current) {
      previewVideoRef.current.srcObject = null;
    }
  };

  const startMediaRecordingFromCanvas = async () => {
    if (recordingStartedRef.current) return;
    recordingStartedRef.current = true;

    try {
      const canvas = document.getElementById("canvasOutput");

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        console.error("Canvas not ready for capture:", canvas?.width, canvas?.height);
        recordingStartedRef.current = false;
        return;
      }

      const videoStream = canvas.captureStream(30);
      const videoTrack = videoStream.getVideoTracks()[0];

      if (!videoTrack) {
        console.error("No video track from canvas");
        recordingStartedRef.current = false;
        return;
      }

      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });

      if (isUnmountedRef.current) {
        audioStream.getTracks().forEach((t) => t.stop());
        recordingStartedRef.current = false;
        return;
      }

      audioStreamRef.current = audioStream;

      const combinedStream = new MediaStream([...videoStream.getVideoTracks(), ...audioStream.getAudioTracks()]);

      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";

      const recorder = new MediaRecorder(combinedStream, { mimeType });
      mediaRecorderRef.current = recorder;
      mediaChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) mediaChunksRef.current.push(e.data);
      };

      recorder.start(100);
    } catch (e) {
      console.error("Recording failed:", e);
      recordingStartedRef.current = false;

      if (!isUnmountedRef.current) {
        const isDenied = e.name === "NotAllowedError" || e.name === "PermissionDeniedError";
        setError(
          isDenied
            ? "Microphone permission was denied. Please allow microphone access and try again."
            : `Recording failed: ${e?.message || "Unknown error"}`,
        );
      }
    }
  };

  const startMediaRecordingFromStream = async (stream) => {
    if (recordingStartedRef.current) return;
    recordingStartedRef.current = true;

    try {
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus" : "video/webm";

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      mediaChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) mediaChunksRef.current.push(e.data);
      };

      recorder.start(100);
    } catch (e) {
      console.error("Recording failed:", e);
      recordingStartedRef.current = false;

      if (!isUnmountedRef.current) {
        const isDenied = e.name === "NotAllowedError" || e.name === "PermissionDeniedError";
        const message = isDenied
          ? "Microphone permission was denied. Please allow microphone access and try again."
          : `Recording failed: ${e?.message || "Unknown error"}`;
        setError(message);
      }
    }
  };

  const stopAndGetRecordingBlob = async () => {
    const rec = mediaRecorderRef.current;
    if (!rec) return null;

    const getBlob = async () => {
      if (mediaChunksRef.current.length === 0) return null;
      const blob = new Blob(mediaChunksRef.current, { type: "video/webm" });
      if (blob.size > 60 * 1024 * 1024) throw new Error("Video size exceeds limit");
      return blob;
    };

    if (rec.state === "inactive") return await getBlob();

    return new Promise((resolve, reject) => {
      rec.onstop = async () => {
        try {
          resolve(await getBlob());
        } catch (e) {
          reject(e);
        }
      };
      rec.stop();
    });
  };

  const handleVideoProcessing = async (blob) => {
    if (!blob || blob.size === 0) {
      throw new Error("Recorded video is empty. Please try again.");
    }

    const pr = await fetch("https://sdk-staging.carenow.healthcare/pivc/get-presigned-url", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        api_key: state?.api_key,
        pivc_token: state?.result?.pivc_token,
        content_type: "video/webm",
      }),
    });

    const pd = await pr.json();
    if (!pd?.data) throw new Error(pd?.message || "Failed to get presigned URL");

    const { url, fields } = pd.data;
    const form = new FormData();
    Object.entries(fields).forEach(([k, v]) => form.append(k, v));
    form.append("file", blob, "recording.webm");
    await fetch(url, { method: "POST", body: form, mode: "no-cors" });
  };

  useEffect(() => {
    if (!moduleState.shouldDoPIVC) {
      setIsConsentGiven(true);
    }
  }, [moduleState.shouldDoPIVC]);

  useEffect(() => {
    if (!moduleState.shouldDoPIVC && scanCompleted) {
      setReadyToProcess(true);
    }
  }, [moduleState.shouldDoPIVC, scanCompleted]);

  useEffect(() => {
    if (!moduleState.shouldDoPIVC) return;

    const load = async () => {
      try {
        const resp = await fetch("https://sdk-staging.carenow.healthcare/pivc/get-consent-text", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            api_key: state?.api_key,
            pivc_token: state?.result?.pivc_token,
            additional_info: { first_name: "", last_name: "" },
            locale: state?.locale || "en-IN",
          }),
        });
        const respJSON = await resp.json();
        if (respJSON?.statusCode?.toString().startsWith("2")) {
          setConsentText(respJSON?.consent_text);
        } else {
          throw new Error(respJSON?.message || "Failed to Fetch Consent Text");
        }
      } catch (e) {
        console.error("Error fetching consent text:", e);
      }
    };

    load();
  }, [state, moduleState.shouldDoPIVC]);

  useEffect(() => {
    const t = setTimeout(() => setPhase("countdown"), 5000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (phase !== "countdown") return;
    let current = 3;
    const interval = setInterval(() => {
      current -= 1;
      setCount(current);
      if (current === 0) {
        clearInterval(interval);
        setPhase("consent");
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  const handleConsentScroll = useCallback(() => {
    const el = consentScrollRef.current;
    if (!el || hasScrolledToBottom) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom]);

  useEffect(() => {
    if (phase !== "consent" || !consentText) return;
    const el = consentScrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 20) {
      setHasScrolledToBottom(true);
    }
  }, [phase, consentText]);

  useEffect(() => {
    const buttonVisible = moduleState.shouldDoPIVC && scanCompleted && hasScrolledToBottom && !isConsentGiven;

    if (!buttonVisible) return;

    const autoSubmissionTimeout = Number.isFinite(state?.autoSubmissionTimeout) && state.autoSubmissionTimeout > 0 ? state.autoSubmissionTimeout : 60;
    setAutoSubmitCountdown(autoSubmissionTimeout);
    const interval = setInterval(() => {
      setAutoSubmitCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsConsentGiven(true);
          setReadyToProcess(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [scanCompleted, hasScrolledToBottom, isConsentGiven, moduleState.shouldDoPIVC, state.autoSubmissionTimeout]);

  useEffect(() => {
    const flowType = state?.flowType;
    isUnmountedRef.current = false;

    if (flowType === "VIDEO_ONLY") {
      const startVideoOnly = async () => {
        try {
          if (isUnmountedRef.current) return;
          setLoading(false);
          setScanCompleted(true);

          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: true,
          });

          if (isUnmountedRef.current) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }

          videoOnlyStreamRef.current = stream;

          const videoEl = previewVideoRef.current;
          videoEl.srcObject = stream;
          await videoEl.play();

          if (isUnmountedRef.current) return;

          setIsVideoOnly(true);

          await delay(300);
          if (isUnmountedRef.current) return;

          await startMediaRecordingFromStream(stream);
        } catch (e) {
          if (isUnmountedRef.current) return;
          console.error("VIDEO_ONLY camera start failed:", e);
          const isDenied = e.name === "NotAllowedError" || e.name === "PermissionDeniedError";
          const message = isDenied
            ? "Camera/microphone permission was denied. Please allow access and try again."
            : e?.message || "Failed to start video stream";
          setError(message);
        }
      };

      startVideoOnly();

      return () => {
        isUnmountedRef.current = true;
        discardRecording();
      };
    }

    faceScan.onFrame((fd) => {
      setScanFrameData(fd);
      if (!recordingStartedRef.current && fd.type !== "") {
        startMediaRecordingFromCanvas();
      }
    });

    faceScan.onError((err) => {
      if (isUnmountedRef.current) return;
      console.error("SDK Error:", err);
      discardRecording();
      setLoading(false);
      setError(err?.message || "An error occurred during the face scan. Please try again.");
    });

    faceScan.onScanFinish(({ raw_intensity, ppg_time, average_fps }) => {
      setScanCompleted(true);
      setScanData({ raw_intensity, ppg_time, average_fps });
    });

    const initFullScan = async () => {
      try {
        if (!state?.result?.scan_token) {
          setError("Invalid Scan Token");
          setLoading(false);
          return;
        }

        await faceScan.startScan(61000, 61000, undefined, undefined, { color: "#fff", drawType: "corner-box" }, 5000);

        setLoading(false);
      } catch (e) {
        if (isUnmountedRef.current) return;
        console.error("Init failed:", e);
        setLoading(false);
        const errMessage = e?.message || "Failed to initialize scan modules";
        if (errMessage !== "Facescan Initialization Error.") setError(errMessage);
      }
    };

    initFullScan();

    return () => {
      isUnmountedRef.current = true;
      discardRecording();
      faceScan.stopScan(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!moduleState.shouldDoFaceMatch) {
      faceMatchDoneRef.current = true;
      setIsFaceMatched(true);
      return;
    }

    if (faceMatchDoneRef.current) return;

    const prevDone = localStorage.getItem("careplix_facematch_done") === "true";
    if (prevDone) {
      faceMatchDoneRef.current = true;
      setIsFaceMatched(true);
      return;
    }

    if (scanFrameData.type !== "scan") return;
    if (scanFrameData.timeElapsed < 15000) return;

    faceMatchDoneRef.current = true;

    const runGate = async () => {
      try {
        const canvasEl = document.getElementById("canvasOutput");
        const offscreen = document.createElement("canvas");
        const scale = Math.min(1, 640 / canvasEl.width);
        offscreen.width = Math.round(canvasEl.width * scale);
        offscreen.height = Math.round(canvasEl.height * scale);
        offscreen.getContext("2d").drawImage(canvasEl, 0, 0, offscreen.width, offscreen.height);

        const blob = await new Promise((r) => offscreen.toBlob(r, "image/jpeg", 0.7));
        if (!blob) throw new Error("Could not capture face image at 15s");

        const base64 = await fileToBase64(new File([blob], "face-15s.jpg", { type: blob.type }));

        const resp = await fetch("https://sdk-staging.carenow.healthcare/facematch/image-upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            api_key: state?.api_key,
            facematch_token: state?.result?.facematch_token,
            candidate_images: [{ image: base64 }],
            run_facematch: true,
          }),
        });

        const data = await resp.json();
        const isMatched = data?.facematch_result?.data?.[0]?.is_matched;

        if (isMatched) {
          localStorage.setItem("careplix_facematch_done", "true");
          setIsFaceMatched(true);
        } else {
          throw new Error(data?.message || "Face authentication failed. Please try again.");
        }
      } catch (e) {
        console.error("Facematch gate failed:", e);
        discardRecording();
        faceScan.stopScan(true);
        setError(e?.message || "Face authentication failed.");
      }
    };

    setTimeout(runGate, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanFrameData.timeElapsed, scanFrameData.type]);

  useEffect(() => {
    if (!readyToProcess) return;
    if (moduleState.shouldDoVitals && (!scanCompleted || !scanData)) return;

    const execute = async () => {
      setAnalyzing(true);

      try {
        if (moduleState.shouldDoVitals) {
          const addScanResp = await fetch("https://sdk-staging.carenow.healthcare/vitals/add-scan", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              scan_token: state?.result?.scan_token,
              api_key: state?.api_key,
              posture: "resting",
              dob: "1998-04-25",
              gender: "male",
              metadata: {
                physiological_scores: { height: "165", weight: "65" },
                ppg_time: scanData.ppg_time,
                raw_intensity: scanData.raw_intensity,
                device: `RPPG_CAREPLIX_FACE_${/iPhone|iPad|iPod|Macintosh|Mac/i.test(navigator.userAgent) ? "IOS" : "ANDROID"}`,
                fps: scanData.average_fps,
              },
            }),
          });

          const addScanRespJSON = await addScanResp.json();
          if (!addScanRespJSON?.statusCode?.toString().startsWith("2")) {
            throw new Error(addScanRespJSON?.message || "Failed to process vitals");
          }
        }

        if (moduleState.shouldDoPIVC) {
          let blob = null;
          try {
            blob = await stopAndGetRecordingBlob();
          } catch (e) {
            throw new Error(`Failed to finalize video recording: ${e?.message || "Unknown error"}`);
          }

          if (!blob) {
            throw new Error("Video recording was not captured. Please try again.");
          }

          await handleVideoProcessing(blob);

          navigate("/scan-result", { state: { ...state }, replace: true });
        } else {
          try {
            await stopAndGetRecordingBlob();
          } catch (e) {
            console.warn("Recording stop error (non-PIVC):", e);
          }

          navigate("/scan-result", { state: { ...state }, replace: true });
        }
      } catch (err) {
        console.error("Flow stopped:", err);
        setAnalyzing(false);
        setError(err?.message || "An error occurred during the face scan. Please try again.");
        discardRecording();
        faceScan.stopScan(true);
      } finally {
        localStorage.removeItem("careplix_facematch_done");
      }
    };

    execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToProcess, scanCompleted, scanData]);

  const progress = scanFrameData.type === "scan" ? scanFrameData.percentage : 0;
  const showConsentButton = moduleState.shouldDoPIVC && scanCompleted && hasScrolledToBottom && !isConsentGiven;

  return (
    <section className="relative h-screen w-screen flex flex-col">
      <div className="fixed top-0 left-0 right-0 p-4 pl-1 bg-primary text-white" style={{ zIndex: 10 }}>
        <CarePlix className="h-14" />
      </div>

      <div className="flex flex-col h-screen pt-20 pb-2.5" style={{ zIndex: 2 }}>
        <div className="flex flex-col h-full lg:max-w-[30rem] mx-auto w-full px-6">
          <div className="relative shrink-0 flex items-center justify-center rounded-2xl overflow-hidden bg-black" style={{ height: "30vh" }}>
            {/* FULL_SCAN: SDK draws directly into this canvas. */}
            <canvas
              id="canvasOutput"
              ref={canvasRef}
              className="h-full w-full object-cover"
              style={{
                display: isVideoOnly ? "none" : "block",
                transform: "scaleX(-1)",
              }}
            />

            {/* VIDEO_ONLY: raw stream piped directly here — native browser rendering. */}
            <video
              ref={previewVideoRef}
              autoPlay
              muted
              playsInline
              className="h-full w-full object-cover"
              style={{
                display: isVideoOnly ? "block" : "none",
                transform: "scaleX(-1)",
              }}
            />

            {isFaceMatched && (
              <span className="absolute top-2 right-2 px-2 py-1 text-xs bg-green-500 text-white rounded-full shadow">Face Matched ✓</span>
            )}
          </div>

          <div className="flex flex-col mt-2 bg-primary rounded-2xl overflow-hidden" style={{ height: "70vh" }}>
            <div
              ref={consentScrollRef}
              id="consent-scroll"
              className="flex-1 overflow-y-auto p-3"
              onScroll={handleConsentScroll}
              style={{
                background: "rgba(255,255,255,.03)",
                border: "1px solid rgba(255,255,255,.08)",
                borderRadius: 14,
              }}>
              {phase === "instruction" && (
                <p className="text-xl font-bold text-red-500 mb-4 p-4">
                  To complete your video consent recording, please read the following text aloud clearly while facing the camera.
                </p>
              )}

              {phase === "countdown" && (
                <div className="flex items-center justify-center h-40">
                  <p className="text-6xl font-bold text-white animate-pulse">{count}</p>
                </div>
              )}

              {phase === "consent" && (
                <>
                  <p className="text-white text-2xl leading-7 whitespace-pre-wrap p-4">{consentText}</p>
                  {!hasScrolledToBottom && <p className="text-center text-sm text-white/50 pb-4 animate-bounce">↓ Scroll down to continue</p>}
                </>
              )}
            </div>

            {showConsentButton && (
              <div className="shrink-0 pt-3 pb-2 px-3 bg-primary">
                <button
                  type="button"
                  onClick={() => {
                    setIsConsentGiven(true);
                    setReadyToProcess(true);
                  }}
                  className="w-full px-5 py-3 rounded-full bg-gradient text-white font-medium shadow-lg">
                  I confirm that I have read and understood the consent statement
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Required by the SDK internally — must stay in DOM, kept invisible */}
      <video id="videoInput" className="absolute" style={{ width: 1, height: 1, opacity: 0, pointerEvents: "none" }} autoPlay muted playsInline />

      <div className="fixed bottom-0 left-0 right-0" style={{ zIndex: 10 }}>
        <div className="relative h-2.5 border-y border-primary bg-white">
          <div
            className="absolute left-0 top-0 bottom-0 rounded-r-full bg-success shadow-inner transition-all duration-1000"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {analyzing && (
        <div className="fixed inset-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-white text-center" style={{ zIndex: 20 }}>
          <img className="shrink-0 h-32" src={Analyzing} alt="Analyzing..." />
          <h4 className="mt-8 text-xl font-bold">Analyzing Data</h4>
          <p className="mt-4 font-light">
            Continuing analysis for
            <br />
            accurate measurements...
          </p>
        </div>
      )}

      {error.length > 0 && (
        <div className="fixed inset-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-white text-center" style={{ zIndex: 20 }}>
          <img className="shrink-0 h-44" src={Failure} alt="Failure..." />
          <p className="mt-8 text-xl font-bold">
            Sorry, we failed to calculate
            <br />
            your measurements
          </p>
          <p className="mt-4 max-h-40 overflow-auto whitespace-pre-line font-light">{error}</p>
          <button
            className="mt-8 w-60 px-5 py-2.5 rounded-full bg-gradient text-white font-medium"
            type="button"
            onClick={() => {
              discardRecording();
              faceScan.stopScan(true);
              postMessage("error", error);
            }}>
            Back
          </button>
        </div>
      )}

      {loading && (
        <div className="fixed inset-0 px-8 py-12 flex flex-col items-center justify-center bg-primary text-white text-center" style={{ zIndex: 20 }}>
          <img className="shrink-0 h-20" src={Loader} alt="Loading..." />
          <h4 className="mt-20 text-xl font-bold">Downloading Scan modules</h4>
          <p className="mt-4 font-light">This might take few seconds....</p>
        </div>
      )}
    </section>
  );
};

export default FaceScan;
